// src/services/voucherService.js (customer side)
// ---------------------------------------------------------------------------
// Reward handling now runs on the server.
//
// This file used to hold the whole reward flow in the browser: it read the
// vouchers collection to see what was available, counted a guest's previous
// claims, wrote the claim document and queued the email. That worked, but it
// meant the Firestore rules had to leave reward codes and guest claim records
// readable by anyone on the internet.
//
// All of that moved into three callable Cloud Functions running with the Admin
// SDK, so the guest's browser never reads a reward code or another guest's
// record. What is left here is a thin, well behaved client.
//
// The guest flow must never be blocked by an error, so every function below
// resolves to a status the screens can act on rather than throwing.
// ---------------------------------------------------------------------------
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";

/**
 * Identify or create the guest. Replaces the old pattern of creating a Firebase
 * Auth account with a password derived from the email address, which meant
 * anyone who knew a guest's email could sign in as them.
 *
 * @returns {Promise<{customerId: string, isReturning: boolean}|null>}
 */
/**
 * Register or recognise a guest.
 *
 * Takes whatever the activation asked for rather than a fixed three. The
 * previous version destructured firstName, lastName and email, so a mobile
 * number or a date of birth added to the details screen would have been
 * collected, validated, and then quietly dropped on the way to the server.
 *
 * `eventId` travels with it so the function can read the activation's own field
 * configuration and check the answers against that, rather than against
 * whatever this call claims. The details screen has no login; the server's
 * check is the one that counts.
 *
 * @param {object} details  the validated values, keyed by field id
 * @param {string} eventId  the activation being scanned
 */
export const registerGuest = async (details, eventId) => {
  try {
    const call = httpsCallable(functions, "registerGuest");
    const res = await call({ ...details, eventId: eventId || null });
    return res.data;
  } catch (error) {
    console.error("registerGuest failed:", error);
    return null;
  }
};

/**
 * Ask for a reward. The server checks the scan limit, the global email cap and
 * whether any codes are left, creates the claim and queues the email.
 *
 * @returns {Promise<{status: "sent"|"limit"|"cap"|"none"|"error"}>}
 */
export const requestReward = async ({
  eventId,
  customerId,
  customerEmail,
  customerName,
  bartenderId,
}) => {
  try {
    const call = httpsCallable(functions, "reserveReward");
    const res = await call({
      eventId,
      customerId,
      customerEmail,
      customerName,
      bartenderId,
    });
    return res.data || { status: "error" };
  } catch (error) {
    console.error("reserveReward failed:", error);
    // Fail open: the guest still finishes the flow, they simply do not get an
    // email. Exactly the behaviour the old client-side path had on error.
    return { status: "error" };
  }
};

/**
 * Exchange a claim token for an actual code. Called when the guest opens the
 * link in their email.
 *
 * @returns {Promise<{code?: string, url?: string, error?: string, alreadyClaimed?: boolean}>}
 */
export const claimVoucher = async (token) => {
  try {
    const call = httpsCallable(functions, "claimReward");
    const res = await call({ token });
    return res.data || { error: "Something went wrong. Please try the link again." };
  } catch (error) {
    console.error("claimReward failed:", error);
    return {
      error:
        "We could not open your reward just now. Please check your connection and try the link again.",
    };
  }
};
